import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAlert } from '../contexts/AlertContext';
import './BooksPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const BooksPage = () => {
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning } = useAlert();
  const [books, setBooks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    description: '',
    summary: ''
  });

  useEffect(() => {
    fetchBooks();
  }, []);

  const fetchBooks = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/books/?limit=100`);
      setBooks(response.data);
    } catch (error) {
      console.error('Error fetching books:', error);
      showError('책 목록을 불러오는 중 오류가 발생했습니다.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE_URL}/api/books/`, formData);
      setFormData({ title: '', author: '', description: '', summary: '' });
      setShowForm(false);
      showSuccess('새 책이 성공적으로 등록되었습니다!');
      fetchBooks();
    } catch (error) {
      console.error('Error creating book:', error);
      showError('책 등록 중 오류가 발생했습니다.');
    }
  };

  const startQuiz = (bookId, difficulty) => {
    navigate(`/quiz/${bookId}/${difficulty}`);
  };

  return (
    <div className="container">
      <div className="page-header">
        <h2>📚 도서 목록</h2>
        <button 
          className="btn btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? '취소' : '+ 새 책 등록'}
        </button>
      </div>

      <section className="features" style={{marginBottom: '30px'}}>
        <div className="features-grid">
          <div className="feature-card fade-in">
            <h3>📖 AI Book Check</h3>
            <p>등록된 도서들의 내용을 바탕으로 AI가 생성한 게임을 진행하세요!</p>
          </div>
          <div className="feature-card fade-in">
            <h3>🎯 다양한 난이도</h3>
            <p>1단계부터 5단계까지 다양한 난이도로 자신의 수준에 맞는 게임을 선택할 수 있습니다.</p>
          </div>
          <div className="feature-card fade-in">
            <h3>📊 학습 분석</h3>
            <p>게임 결과를 통해 독해력과 학습 성향을 분석받아보세요.</p>
          </div>
        </div>
      </section>

      {showForm && (
        <div className="card">
          <h3>새 책 등록</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">책 제목</label>
              <input
                type="text"
                className="form-input"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">저자</label>
              <input
                type="text"
                className="form-input"
                value={formData.author}
                onChange={(e) => setFormData({...formData, author: e.target.value})}
                required
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">책 소개</label>
              <textarea
                className="form-input"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows="3"
                required
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">줄거리</label>
              <textarea
                className="form-input"
                value={formData.summary}
                onChange={(e) => setFormData({...formData, summary: e.target.value})}
                rows="5"
                placeholder="책의 줄거리를 자세히 입력해주세요. 더 자세할수록 좋은 게임이 생성됩니다."
                required
              />
            </div>
            
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                취소
              </button>
              <button type="submit" className="btn btn-primary">
                등록
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="books-grid">
        {books.map((book) => (
          <div key={book.id} className="card">
            <h3>{book.title}</h3>
            <p><strong>저자:</strong> {book.author}</p>
            <p>{book.description}</p>
            
            {/* 책 정보 */}
            <div className="book-info">
              <p><strong>등록일:</strong> {new Date(book.created_at).toLocaleDateString()}</p>
              <p><strong>줄거리 길이:</strong> {book.summary ? book.summary.length : 0}자</p>
            </div>
            
            {/* 게임 시작 버튼들 */}
            <div className="quiz-buttons">
              <h4>게임 시작하기:</h4>
              <div className="btn-group">
                <button 
                  className="btn btn-info"
                  onClick={() => startQuiz(book.id, 'very_easy')}
                >
                  ⚪ 1단계 게임
                </button>
                <button 
                  className="btn btn-success"
                  onClick={() => startQuiz(book.id, 'easy')}
                >
                  🟢 2단계 게임
                </button>
                <button 
                  className="btn btn-warning"
                  onClick={() => startQuiz(book.id, 'normal')}
                >
                  🟡 3단계 게임
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={() => startQuiz(book.id, 'hard')}
                >
                  🔴 4단계 게임
                </button>
                <button 
                  className="btn btn-dark"
                  onClick={() => startQuiz(book.id, 'very_hard')}
                >
                  ⚫ 5단계 게임
                </button>
              </div>
              
              <div className="quiz-info">
                <p className="info-text">
                  관리자가 생성한 문제은행에서 게임이 출제됩니다.
                </p>
              </div>
            </div>
          </div>
        ))}
        
        {books.length === 0 && (
          <div className="empty-state">
            <h3>등록된 책이 없습니다</h3>
            <p>새 책을 등록하여 게임을 시작해보세요!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BooksPage; 